import { DeviceTypeKeys } from './device-types-attributes';

export interface DeviceList {
  gates: DeviceListItem[];
  szts: DeviceListItem[];
  sims: DeviceListItem[];
  pdfs: DeviceListItem[];
}

export interface DeviceListItem {
  gateDeviceId?: number;
  gateExtDeviceId?: string; // ez is jön
  gateCustomerId?: number;
  gateCustomerName?: string;
  gateFactorySn?: string;
  gateLogisticStatus?: string;
  gateLogisticStatusDesc?: string;
  sztDeviceId?: number;
  sztExtDeviceId?: string; // ez is jön
  sztCustomerId?: number;
  sztCustomerName?: string;
  sztLogisticStatus?: string;
  sztLogisticStatusDesc?: string;
  simDeviceId?: number;
  simExtDeviceId?: string;
  simCustomerId?: number;
  simCustomerName?: string;
  simLogisticStatus?: string;
  simLogisticStatusDesc?: string;
  writeRight?: number;

  pdfId?: number;
  pdfName?: string;
  createdTimestamp?: Date;

  operationStatus?: DeviceStatus;

  externalDeviceTypeCode: DeviceTypeKeys;
}

export interface DeviceCardBaseItem {
  externalDeviceTypeCode: DeviceTypeKeys;
  deviceId: number;
  externalDeviceId: string;
  customerId: number;
  customerName: string;
  logisticStatus?: string;
  logisticStatusDesc?: string;
  writeRight?: number;
}

export interface DeviceCardItem extends DeviceListItem, DeviceCardBaseItem {}

export function getDeviceId(device: DeviceListItem): number {
  switch (device.externalDeviceTypeCode) {
    case DeviceTypeKeys.GATE:
      return device.gateDeviceId ?? 0;
    case DeviceTypeKeys.SZT:
      return device.sztDeviceId ?? 0;
    case DeviceTypeKeys.SIMCARD:
      return device.simDeviceId ?? 0;
    default:
      throw new Error('Unknown device type');
  }
}

export function getExternalDeviceId(device: DeviceListItem): string {
  switch (device.externalDeviceTypeCode) {
    case DeviceTypeKeys.GATE:
      return device.gateExtDeviceId ?? '';
    case DeviceTypeKeys.SZT:
      return device.sztExtDeviceId ?? '';
    case DeviceTypeKeys.SIMCARD:
      return device.simExtDeviceId ?? '';
    default:
      throw new Error('Unknown device type');
  }
}

export function getCustomerId(device: DeviceListItem): number {
  switch (device.externalDeviceTypeCode) {
    case DeviceTypeKeys.GATE:
      return device.gateCustomerId ?? 0;
    case DeviceTypeKeys.SZT:
      return device.sztCustomerId ?? 0;
    case DeviceTypeKeys.SIMCARD:
      return device.simCustomerId ?? 0;
    default:
      throw new Error('Unknown device type');
  }
}

export function getCustomerName(device: DeviceListItem): string {
  switch (device.externalDeviceTypeCode) {
    case DeviceTypeKeys.GATE:
      return device.gateCustomerName ?? '';
    case DeviceTypeKeys.SZT:
      return device.sztCustomerName ?? '';
    case DeviceTypeKeys.SIMCARD:
      return device.simCustomerName ?? '';
    default:
      throw new Error('Unknown device type');
  }
}

export function getLogisticStatus(device: DeviceListItem): string {
  switch (device.externalDeviceTypeCode) {
    case DeviceTypeKeys.GATE:
      return device.gateLogisticStatus ?? '';
    case DeviceTypeKeys.SZT:
      return device.sztLogisticStatus ?? '';
    case DeviceTypeKeys.SIMCARD:
      return device.simLogisticStatus ?? '';
    default:
      throw new Error('Unknown device type');
  }
}

export function getLogisticStatusDesc(device: DeviceListItem): string {
  switch (device.externalDeviceTypeCode) {
    case DeviceTypeKeys.GATE:
      return device.gateLogisticStatusDesc ?? '';
    case DeviceTypeKeys.SZT:
      return device.sztLogisticStatusDesc ?? '';
    case DeviceTypeKeys.SIMCARD:
      return device.simLogisticStatusDesc ?? '';
    default:
      throw new Error('Unknown device type');
  }
}

export function convertToDeviceCardItem(item: DeviceListItem): DeviceCardItem {
  return {
    ...item,
    deviceId: getDeviceId(item),
    externalDeviceId: getExternalDeviceId(item),
    customerId: getCustomerId(item),
    customerName: getCustomerName(item),
    logisticStatus: getLogisticStatus(item),
    logisticStatusDesc: getLogisticStatusDesc(item),
  };
}

export function convertToDeviceCardItemWithType(
  item: DeviceListItem,
  type: DeviceTypeKeys
): DeviceCardItem {
  return {
    ...item,
    deviceId: getDeviceId(item),
    externalDeviceId: getExternalDeviceId(item),
    customerId: getCustomerId(item),
    customerName: getCustomerName(item),
    externalDeviceTypeCode: type,
  };
}

export const DeviceStatusKeys = ['OK', 'WARNING', 'ERROR'] as const;
export type DeviceStatus = (typeof DeviceStatusKeys)[number];

export interface DeviceStatusAttr {
  icon: string;
}

export const DeviceStatusData: Record<DeviceStatus, DeviceStatusAttr> = {
  ERROR: {
    icon: 'error_outlined',
  },
  WARNING: {
    icon: 'warning_amber',
  },
  OK: {
    icon: 'check_circle_outlined',
  },
};

export interface DeviceFilterItem {
  index: number;
  control: keyof DeviceCardItem;
  translate: string;
  activated?: boolean;
  distinctFilter?: boolean;
}

export const DefaultActivatedFilterItemKeysMobile = ['externalDeviceId'];
export const DeviceFilterItems: DeviceFilterItem[] = [
  {
    index: 0,
    control: 'externalDeviceId',
    translate: 'ext-device-id',
    activated: true,
  },
  {
    index: 1,
    control: 'deviceId',
    translate: 'device-id',
    activated: true,
  },
  {
    index: 2,
    control: 'gateFactorySn',
    translate: 'factory-sn',
    activated: true,
    distinctFilter: true,
  },
  {
    index: 3,
    control: 'logisticStatus',
    translate: 'logistic-status',
    activated: false,
    distinctFilter: true,
  },
  {
    index: 4,
    control: 'logisticStatusDesc',
    translate: 'logistic-status-desc',
    activated: false,
    distinctFilter: true,
  },
  {
    index: 5,
    control: 'customerName',
    translate: 'customer-name',
    activated: true,
  },
];
